import { env } from "@/config/env";

type ApiError = {
  message?: string;
  code?: string;
  status?: number;
};

type AuthApiResponse<T> = {
  data: T | null;
  error: ApiError | null;
};

export interface PhoneMfaFactor {
  id: string;
  phone?: string;
  status?: string;
  friendly_name?: string;
}

interface AuthSettingsResponse {
  external?: {
    phone?: boolean;
  };
  sms_provider?: string;
}

export interface PhoneAuthSettings {
  phoneEnabled: boolean;
  smsProvider: string | null;
}

function getApiBaseUrl(): string {
  return env.API_BASE_URL || '/api';
}

async function callAuthApi<T>(path: string, init?: RequestInit): Promise<AuthApiResponse<T>> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  const payload = await response.json().catch(() => ({ data: null, error: { message: response.statusText } })) as AuthApiResponse<T>;

  if (!response.ok && !payload.error) {
    return {
      data: null,
      error: {
        message: response.statusText,
        status: response.status,
      },
    };
  }

  return payload;
}

export function normalizePhoneDigits(value: string): string {
  const digitsOnly = value.replace(/\D/g, "");

  if (!digitsOnly) {
    return "";
  }

  if (digitsOnly.startsWith("00")) {
    return digitsOnly.slice(2);
  }

  if (digitsOnly.length === 8) {
    return `852${digitsOnly}`;
  }

  return digitsOnly;
}

export function normalizePhoneE164(value: string): string {
  const digits = normalizePhoneDigits(value);
  return digits ? `+${digits}` : "";
}

export async function getPhoneAuthSettings(): Promise<PhoneAuthSettings> {
  const response = await callAuthApi<AuthSettingsResponse>('/v1/auth/phone/settings');

  if (response.error || !response.data) {
    throw new Error(response.error?.message || 'Failed to load auth settings');
  }

  const payload = response.data;

  return {
    phoneEnabled: Boolean(payload.external?.phone),
    smsProvider: payload.sms_provider ?? null,
  };
}

export function getPhoneAuthErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "Failed to send WhatsApp login code";
  }

  const authError = error as { code?: string; message?: string; status?: number };

  switch (authError.code) {
    case "phone_provider_disabled":
      return "WhatsApp login is disabled in Supabase Auth Providers. Enable Phone before using WhatsApp login.";
    case "sms_send_failed":
      return "WhatsApp login is enabled, but the OTP delivery step failed. Check the Send SMS hook and WhatsApp bridge configuration.";
    case "validation_failed":
      return "Enter your WhatsApp number as an 8-digit Hong Kong mobile number, or include a full international number starting with +.";
    case "over_sms_send_rate_limit":
      return "Too many WhatsApp login codes were requested for this number. Wait a minute and try again.";
    case "hook_timeout":
      return "WhatsApp login delivery timed out. Wait a minute and try again.";
    case "unexpected_failure":
      return "WhatsApp login is temporarily unavailable. Please try again shortly.";
    case "otp_expired":
      return "This WhatsApp login code has expired or has already been used. Request a new code and try again.";
    default:
      if (authError.message?.includes('Unexpected status code returned from hook')) {
        return 'WhatsApp login is temporarily unavailable. Please try again shortly.';
      }
      if (authError.message?.includes('Service currently unavailable due to hook')) {
        return 'WhatsApp login delivery is currently unavailable. Please try again shortly.';
      }
      if (authError.message?.includes('token has expired or is invalid')) {
        return 'This WhatsApp login code has expired or has already been used. Request a new code and try again.';
      }
      return authError.message ?? "Failed to send WhatsApp login code";
  }
}

export async function sendPhoneOtp(phoneNumber: string) {
  return callAuthApi('/v1/auth/phone/otp', {
    method: 'POST',
    body: JSON.stringify({
      phoneNumber: normalizePhoneE164(phoneNumber),
    }),
  });
}

export async function verifyPhoneOtp(phoneNumber: string, code: string) {
  return callAuthApi('/v1/auth/phone/verify', {
    method: 'POST',
    body: JSON.stringify({
      phoneNumber: normalizePhoneE164(phoneNumber),
      code,
    }),
  });
}

export async function getAuthenticatorAssuranceLevel() {
  return callAuthApi('/v1/auth/mfa/aal');
}

export async function listPhoneFactors() {
  const { data, error } = await callAuthApi<{ phone?: PhoneMfaFactor[] }>('/v1/auth/mfa/factors');

  if (error) {
    throw Object.assign(new Error(error.message || 'Failed to load MFA factors'), error);
  }

  return (data?.phone ?? []) as PhoneMfaFactor[];
}

export async function enrollPhoneMfa(phoneNumber: string) {
  return callAuthApi<{ id: string }>('/v1/auth/mfa/enroll', {
    method: 'POST',
    body: JSON.stringify({
      phoneNumber: normalizePhoneE164(phoneNumber),
    }),
  });
}

export async function challengePhoneMfa(factorId: string) {
  return callAuthApi<{ id: string }>('/v1/auth/mfa/challenge', {
    method: 'POST',
    body: JSON.stringify({ factorId }),
  });
}

export async function verifyPhoneMfa(factorId: string, challengeId: string, code: string) {
  return callAuthApi('/v1/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({
      factorId,
      challengeId,
      code,
    }),
  });
}

export async function unenrollPhoneMfa(factorId: string) {
  return callAuthApi('/v1/auth/mfa/unenroll', {
    method: 'POST',
    body: JSON.stringify({ factorId }),
  });
}